import { t } from "@travily/i18n";
import { FileDropField } from "@ui/components/FileDropField";
import { Heading } from "@ui/components/Heading";
import { GlobeIcon } from "@ui/components/Icon";
import { RadioGroupWithFollowUp } from "@ui/components/RadioGroupWithFollowUp";
import { WizardTextField } from "@ui/components/WizardTextField";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { ListingFieldCopy } from "./types";

const COLOR = "green" as const;

export type DeliveryKind = "file" | "link";
type SelectionSize = "3xl" | "4xl" | "5xl";

/**
 * Content is a product-only field — `catalog_services` has no delivery columns —
 * so unlike the other fields its copy is not keyed by listing type.
 */
export function contentFieldCopy(): ListingFieldCopy {
  return {
    heading: t("web.listings.fields.content.heading"),
    helper: t("web.listings.fields.content.helper"),
  };
}

export interface ContentFieldProps {
  /** Selected delivery mode. */
  deliveryKind: DeliveryKind;
  /** Current delivery URL for the link input. */
  deliveryUrl: string;
  /** File field error; when present the drop field renders invalid. */
  fileError?: string;
  /** Name of the attached or just-picked file, if any. */
  fileName?: string;
  /** Size in bytes of the just-picked file, if any. */
  fileSize?: number;
  /** Whether a direct upload is in flight. */
  isUploading: boolean;
  /** Link field error; when present the input renders invalid. */
  linkError?: string;
  /** Fires on link input change with the new value. */
  onDeliveryUrlChange: (value: string) => void;
  /** Clears the currently attached file. */
  onFileRemove: () => void;
  /** Fires with the chosen `File` to begin the upload. */
  onFileSelect: (file: File) => void;
  /** Fires when the delivery mode changes (raw string from the radio group). */
  onKindChange: (value: string) => void;
  /** Upload progress, 0–100. */
  progress: number;
  /**
   * Radio tile scale. Defaults to the wizard's breakpoint-driven sizing; the
   * editor's section pane is narrower, so it pins the compact set.
   */
  size?: SelectionSize;
}

export function ContentField({
  deliveryKind,
  deliveryUrl,
  fileError,
  fileName,
  fileSize,
  isUploading,
  linkError,
  onDeliveryUrlChange,
  onFileRemove,
  onFileSelect,
  onKindChange,
  progress,
  size,
}: ContentFieldProps) {
  const isAboveMd = useBreakpoint("md");
  const isAboveLg = useBreakpoint("lg");
  const selectionGroupSize: SelectionSize = size ?? (isAboveLg ? "5xl" : isAboveMd ? "4xl" : "3xl");

  const reducedMotion = useReducedMotion() ?? false;

  // Each option reveals a noun-titled divider ("File" / "Link") over its input — the same centered-rule
  // section header the pricing stage uses for "Price" — so the chosen deliverable is framed, not bare.
  const fileFollowUp = (
    <div className="text-left">
      <Heading.Root color="secondary" divider="both" size="sm">
        <Heading.Title>{t("web.listings.fields.content.file_divider_label")}</Heading.Title>
      </Heading.Root>
      <FileDropField
        aria-label={t("web.listings.fields.content.file_drop_title")}
        className="mt-8"
        color={COLOR}
        errorMessage={fileError}
        fileName={fileName}
        fileSize={fileSize}
        hint={t("web.listings.fields.content.file_drop_hint")}
        isUploading={isUploading}
        onFileSelect={onFileSelect}
        onRemove={onFileRemove}
        progress={progress}
        removeLabel={t("web.listings.fields.content.file_remove")}
        replaceLabel={t("web.listings.fields.content.file_replace")}
        title={t("web.listings.fields.content.file_drop_title")}
      />
    </div>
  );

  const linkFollowUp = (
    <div className="text-left">
      <Heading.Root color="secondary" divider="both" size="sm">
        <Heading.Title>{t("web.listings.fields.content.link_divider_label")}</Heading.Title>
      </Heading.Root>
      <WizardTextField
        className="mt-8"
        color={COLOR}
        errorMessage={linkError}
        isInvalid={Boolean(linkError)}
        isLabelHidden
        label={t("web.listings.fields.content.link_label")}
        leadIcon={<GlobeIcon />}
        onChange={onDeliveryUrlChange}
        placeholder={t("web.listings.fields.content.url_placeholder")}
        type="url"
        value={deliveryUrl}
      />
    </div>
  );

  // file ⇄ link is always one panel or the other (never collapsed), so it's a content swap, not an
  // open/close. `layout` eases the height change between the drop card and the url field while the inner
  // crossfade trades their contents — matching the pricing ledger's motion. Reduced motion drops both.
  const deliveryReveal = (
    <motion.div
      className="relative"
      layout={reducedMotion ? false : "size"}
      transition={{ duration: reducedMotion ? 0 : 0.28, ease: "easeOut" }}
    >
      <AnimatePresence initial={false} mode="popLayout">
        <motion.div
          key={deliveryKind}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.18 }}
        >
          {deliveryKind === "file" ? fileFollowUp : linkFollowUp}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );

  return (
    <RadioGroupWithFollowUp
      align="horizontal"
      color={COLOR}
      followUp={{ presentation: "content", triggerValue: deliveryKind, content: deliveryReveal }}
      items={[
        { value: "file", label: t("web.listings.fields.content.add_file") },
        { value: "link", label: t("web.listings.fields.content.add_link") },
      ]}
      onChange={onKindChange}
      size={selectionGroupSize}
      value={deliveryKind}
    />
  );
}
